import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import {
  AlertTriangle, BookOpen, Check, ChevronDown, ChevronUp, ExternalLink, Loader2,
  Play, Square, ThumbsDown, ThumbsUp, X, CheckCheck, Database,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import {
  Button, Card, EmptyState, ErrorState, LoadingState,
} from '@/components/ui'
import { DocumentDetailModal } from '@/components/documents/DocumentDetailModal.tsx'
import type { CachedDocument } from '@/api/sapDocuments.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface TenantScanGroup {
  tenantId: string
  tenantCode: string
  tenantName: string
  total: number
  quoteIds: string[]
  sapTotal: number
  alreadyProcessed: number
}

interface PrepareResult {
  tenants: TenantScanGroup[]
  totalUnprocessed: number
  alreadyProcessed: number
  tenantErrors?: Record<string, string>
}

interface BatchResult {
  processed: number
  newCandidates: number
  skipped: number
  scored: number
  errors: string[]
}

interface QuoteRagCandidate {
  id: string
  quoteId: string
  quoteTitle: string
  contentText: string
  qualityScore: number | null
  qualityReason: string | null
  status: 'pending' | 'approved' | 'rejected'
  knowledgeSourceId: string | null
  reviewedAt: string | null
  createdAt: string
}

// ─── Parse quoteId into SAP ref or local UUID ─────────────────────────────────

interface SapRef { type: 'sap'; tenantId: string; docEntry: number }
interface LocalRef { type: 'local'; quoteId: string }
type QuoteRef = SapRef | LocalRef

function parseQuoteRef(quoteId: string): QuoteRef {
  if (quoteId.startsWith('sap:')) {
    // format: sap:{tenantId}:{docEntry}
    const rest = quoteId.slice(4)
    const sep = rest.lastIndexOf(':')
    if (sep > 0) {
      const tenantId = rest.slice(0, sep)
      const docEntry = parseInt(rest.slice(sep + 1), 10)
      if (tenantId && !isNaN(docEntry)) return { type: 'sap', tenantId, docEntry }
    }
  }
  return { type: 'local', quoteId }
}

// ─── Per-tenant progress row ──────────────────────────────────────────────────

interface TenantProgress {
  tenantCode: string
  tenantName: string
  total: number
  done: number
}

// ─── Score badge ──────────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) return <span className="text-gray-400 text-xs">–</span>
  const pct = Math.round(score * 100)
  const color =
    score >= 0.75 ? 'bg-green-100 text-green-700' :
    score >= 0.5  ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${color}`}>
      {pct}%
    </span>
  )
}

// ─── Candidate Row ────────────────────────────────────────────────────────────

function CandidateRow({
  candidate, onApprove, onReject, approving, rejecting,
}: {
  candidate: QuoteRagCandidate
  onApprove: (id: string) => void
  onReject: (id: string) => void
  approving: boolean
  rejecting: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const isPending = candidate.status === 'pending'
  const ref = parseQuoteRef(candidate.quoteId)

  const sapDocStub: CachedDocument | null = ref.type === 'sap' ? {
    id: `sap:${ref.tenantId}:${ref.docEntry}`,
    tenantId: ref.tenantId,
    docType: 'quotation',
    sapDocEntry: ref.docEntry,
    sapDocNum: null,
    cardCode: null,
    cardName: candidate.quoteTitle,
    docDate: null,
    docDueDate: null,
    docTotal: null,
    docCurrency: null,
    docStatus: null,
    isClosed: false,
    isCancelled: false,
    comments: null,
    linesJson: null,
    fullDataJson: null,
    lastSyncedAt: null,
    createdAt: candidate.createdAt,
    updatedAt: candidate.createdAt,
    source: 'sap',
  } : null

  return (
    <>
    {sapDocStub && (
      <DocumentDetailModal
        doc={sapDocStub}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        onConvert={() => {}}
      />
    )}
    <div className={cn(
      'border rounded-lg overflow-hidden',
      candidate.status === 'approved' && 'border-green-200 bg-green-50/30',
      candidate.status === 'rejected' && 'border-gray-200 bg-gray-50 opacity-60',
      candidate.status === 'pending'  && 'border-gray-200 bg-white',
    )}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-shrink-0 w-12">
          <ScoreBadge score={candidate.qualityScore} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{candidate.quoteTitle}</p>
          {candidate.qualityReason && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{candidate.qualityReason}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          {candidate.status === 'approved' && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
              <Check className="w-3.5 h-3.5" /> Im Wissensspeicher
            </span>
          )}
          {candidate.status === 'rejected' && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
              <X className="w-3.5 h-3.5" /> Abgelehnt
            </span>
          )}
        </div>
        {/* Link to offer */}
        <div className="flex-shrink-0">
          {ref.type === 'sap' ? (
            <button
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              title="Angebot in SAP anzeigen"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Angebot
            </button>
          ) : (
            <Link
              to={`/auftraege/angebot/${candidate.quoteId}`}
              target="_blank"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              title="Angebot öffnen"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Angebot
            </Link>
          )}
        </div>
        {isPending && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              variant="ghost" size="xs"
              onClick={() => onApprove(candidate.id)}
              disabled={approving || rejecting}
              leadingIcon={approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
            >
              Übernehmen
            </Button>
            <Button
              variant="ghost" size="xs"
              onClick={() => onReject(candidate.id)}
              disabled={approving || rejecting}
              leadingIcon={<ThumbsDown className="w-3.5 h-3.5" />}
            >
              Ablehnen
            </Button>
          </div>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
        >
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <pre className="mt-3 text-xs text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 rounded p-3 max-h-64 overflow-y-auto leading-relaxed">
            {candidate.contentText}
          </pre>
        </div>
      )}
    </div>
    </>
  )
}

// ─── Main Section ─────────────────────────────────────────────────────────────

const BATCH_SIZE = 10

interface CandidatesResponse {
  candidates: QuoteRagCandidate[]
  counts: { pending: number; approved: number; rejected: number; total: number }
}

export function QuoteRagSection() {
  const queryClient = useQueryClient()
  const [filterStatus, setFilterStatus] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending')
  const [minScore, setMinScore] = useState(0.6)
  const [actioningIds, setActioningIds] = useState<Set<string>>(new Set())
  const [approveError, setApproveError] = useState<string | null>(null)

  // Scan state
  const [scanning, setScanning] = useState(false)
  const [rescan, setRescan] = useState(false)
  const [tenantProgress, setTenantProgress] = useState<TenantProgress[]>([])
  const [scanStats, setScanStats] = useState<{ newCandidates: number; skipped: number; scored: number; errors: string[]; alreadyProcessed: number; sapTotalByTenant: Record<string, number> } | null>(null)
  const [tenantErrors, setTenantErrors] = useState<Record<string, string>>({})
  const stopRef = useRef(false)

  const { data: candidatesData, isLoading, error } = useQuery<CandidatesResponse>({
    queryKey: ['quote-rag-candidates', filterStatus],
    queryFn: () =>
      apiClient.get('/articles/rag/candidates', {
        params: filterStatus !== 'all' ? { status: filterStatus } : {},
      }).then((r) => r.data),
    retry: false,
  })
  const candidates = candidatesData?.candidates ?? []
  const dbCounts = candidatesData?.counts

  const approveMutation = useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.post(`/articles/rag/candidates/${id}/approve`, {}).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-rag-candidates'] })
      setApproveError(null)
    },
    onError: (err) => setApproveError(err.message),
  })

  const rejectMutation = useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.post(`/articles/rag/candidates/${id}/reject`, {}).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quote-rag-candidates'] }),
  })

  const approveAllMutation = useMutation<{ approved: number; errors: string[] }>({
    mutationFn: () => apiClient.post('/articles/rag/candidates/approve-all', { minScore }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-rag-candidates'] })
      setApproveError(null)
    },
  })

  const handleApprove = async (id: string) => {
    setActioningIds((s) => new Set(s).add(id))
    setApproveError(null)
    try {
      await approveMutation.mutateAsync(id)
    } catch {
      // Error is captured via onError above and shown in the UI
    } finally {
      setActioningIds((s) => { const n = new Set(s); n.delete(id); return n })
    }
  }

  const handleReject = async (id: string) => {
    setActioningIds((s) => new Set(s).add(id))
    try { await rejectMutation.mutateAsync(id) } finally {
      setActioningIds((s) => { const n = new Set(s); n.delete(id); return n })
    }
  }

  // ─── Step-by-step scan ──────────────────────────────────────────────────────

  const startScan = async () => {
    setScanning(true)
    stopRef.current = false
    setScanStats(null)
    setTenantProgress([])
    setTenantErrors({})

    try {
      // Phase 1: prepare — get all unprocessed quote IDs per tenant
      const prepare: PrepareResult = await apiClient.post('/articles/rag/scan/prepare', { rescan }).then((r) => r.data)

      // Collect per-tenant SAP errors from the prepare phase
      const allErrors: string[] = []
      if (prepare.tenantErrors && Object.keys(prepare.tenantErrors).length > 0) {
        setTenantErrors(prepare.tenantErrors)
        for (const [tenantCode, errMsg] of Object.entries(prepare.tenantErrors)) {
          allErrors.push(`SAP-Verbindung ${tenantCode}: ${errMsg}`)
        }
      }

      const sapTotalByTenant: Record<string, number> = {}
      for (const t of prepare.tenants) {
        sapTotalByTenant[t.tenantCode] = t.sapTotal ?? 0
      }

      if (prepare.totalUnprocessed === 0) {
        setScanStats({ newCandidates: 0, skipped: 0, scored: 0, errors: allErrors, alreadyProcessed: prepare.alreadyProcessed ?? 0, sapTotalByTenant })
        setScanning(false)
        return
      }

      // Init progress state
      setTenantProgress(prepare.tenants.map((t) => ({
        tenantCode: t.tenantCode,
        tenantName: t.tenantName,
        total: t.total,
        done: 0,
      })))

      let totalNewCandidates = 0
      let totalSkipped = 0
      let totalScored = 0

      // Phase 2: process each tenant batch by batch
      for (const tenantGroup of prepare.tenants) {
        if (stopRef.current) break

        const { quoteIds, tenantCode } = tenantGroup

        for (let offset = 0; offset < quoteIds.length; offset += BATCH_SIZE) {
          if (stopRef.current) break

          const batch = quoteIds.slice(offset, offset + BATCH_SIZE)
          try {
            const result: BatchResult = await apiClient.post('/articles/rag/scan/batch', { quoteIds: batch }).then((r) => r.data)
            totalNewCandidates += result.newCandidates
            totalSkipped += result.skipped ?? 0
            totalScored += result.scored ?? 0
            allErrors.push(...result.errors)

            // Update progress for this tenant
            setTenantProgress((prev) =>
              prev.map((tp) =>
                tp.tenantCode === tenantCode
                  ? { ...tp, done: Math.min(tp.done + batch.length, tp.total) }
                  : tp,
              ),
            )
          } catch (err) {
            allErrors.push(`Batch ${offset}-${offset + BATCH_SIZE} (${tenantCode}): ${err instanceof Error ? err.message : String(err)}`)
          }
        }
      }

      setScanStats({ newCandidates: totalNewCandidates, skipped: totalSkipped, scored: totalScored, errors: allErrors, alreadyProcessed: prepare.alreadyProcessed ?? 0, sapTotalByTenant })
      queryClient.invalidateQueries({ queryKey: ['quote-rag-candidates'] })
    } catch (err) {
      setScanStats({ newCandidates: 0, skipped: 0, scored: 0, errors: [err instanceof Error ? err.message : String(err)], alreadyProcessed: 0, sapTotalByTenant: {} })
    } finally {
      setScanning(false)
    }
  }

  const stopScan = () => { stopRef.current = true }

  const totalDone = tenantProgress.reduce((s, t) => s + t.done, 0)
  const totalQuotes = tenantProgress.reduce((s, t) => s + t.total, 0)

  return (
    <div className="space-y-5">
      {/* Header card */}
      <Card>
        <Card.Header>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-blue-500" />
                Angebote → RAG-Wissensspeicher
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Scannt alle Angebote aller Mandanten, bewertet die Textqualität und schlägt
                gute Beispiele als Stil-Vorlagen für den KI-Assistenten vor.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!scanning && (
                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rescan}
                    onChange={(e) => setRescan(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Bereits gescannte erneut prüfen
                </label>
              )}
              {scanning ? (
                <Button variant="secondary" size="sm" onClick={stopScan} leadingIcon={<Square className="w-4 h-4" />}>
                  Abbrechen
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={startScan} leadingIcon={<Play className="w-4 h-4" />}>
                  Angebote scannen
                </Button>
              )}
            </div>
          </div>
        </Card.Header>

        {/* Per-tenant progress */}
        {(scanning || (tenantProgress.length > 0 && scanStats != null)) && tenantProgress.length > 0 && (
          <Card.Body>
            <div className="space-y-2">
              {tenantProgress.map((tp) => (
                <div key={tp.tenantCode} className="flex items-center gap-3">
                  <span className="text-xs font-mono font-semibold text-gray-700 w-10 flex-shrink-0">{tp.tenantCode}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: tp.total > 0 ? `${Math.round((tp.done / tp.total) * 100)}%` : '0%' }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap w-28 text-right">
                    {tp.done.toLocaleString('de')} / {tp.total.toLocaleString('de')} Angebote
                  </span>
                </div>
              ))}

              {scanning && (
                <div className="flex items-center gap-2 pt-1 text-xs text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {totalDone.toLocaleString('de')} von {totalQuotes.toLocaleString('de')} verarbeitet…
                </div>
              )}
            </div>
          </Card.Body>
        )}

        {/* Tenant SAP errors */}
        {Object.keys(tenantErrors).length > 0 && (
          <Card.Body>
            <div className="space-y-2">
              {Object.entries(tenantErrors).map(([code, msg]) => (
                <div key={code} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                  <span>
                    <strong>SAP-Fehler für Mandant {code}:</strong> {msg}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        )}

        {/* Scan result summary */}
        {scanStats && !scanning && (
          <Card.Body>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="text-gray-600">
                Angebote in SAP: <strong>{(totalQuotes + scanStats.alreadyProcessed).toLocaleString('de')}</strong>
              </span>
              {scanStats.alreadyProcessed > 0 && (
                <span className="text-gray-500">
                  Bereits verarbeitet: <strong>{scanStats.alreadyProcessed.toLocaleString('de')}</strong>
                  {totalQuotes === 0 && (
                    <span className="text-xs font-normal ml-1">– „Bereits gescannte erneut prüfen" aktivieren um erneut zu scannen</span>
                  )}
                </span>
              )}
              {scanStats.skipped > 0 && (
                <span className="text-amber-600">
                  Übersprungen: <strong>{scanStats.skipped.toLocaleString('de')}</strong>
                  <span className="text-xs font-normal ml-1">(keine verwertbaren Positionen)</span>
                </span>
              )}
              <span className="text-blue-600">
                Bewertet: <strong>{scanStats.scored.toLocaleString('de')}</strong>
              </span>
              <span className="text-green-700">
                Neue Kandidaten: <strong>{scanStats.newCandidates}</strong>
              </span>
              {/* Per-tenant SAP totals for diagnostics */}
              {Object.keys(scanStats.sapTotalByTenant).length > 0 && (
                <details className="text-gray-500 text-xs w-full mt-1">
                  <summary className="cursor-pointer font-medium">SAP-Details pro Mandant</summary>
                  <ul className="mt-1 space-y-0.5 list-disc list-inside">
                    {Object.entries(scanStats.sapTotalByTenant).map(([code, total]) => (
                      <li key={code}><strong>{code}</strong>: {total.toLocaleString('de')} Angebote in SAP</li>
                    ))}
                  </ul>
                </details>
              )}
              {scanStats.errors.length > 0 && (
                <details className="text-red-600 text-xs w-full">
                  <summary className="cursor-pointer font-medium">{scanStats.errors.length} Fehler</summary>
                  <ul className="mt-1 space-y-0.5 max-h-24 overflow-y-auto list-disc list-inside">
                    {scanStats.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          </Card.Body>
        )}
      </Card>

      {/* DB diagnostic count — always visible so anomalies are immediately obvious */}
      {dbCounts && (
        <div className="flex items-center gap-4 text-xs text-gray-500 px-1">
          <span className="font-medium text-gray-600">Datenbank-Stand:</span>
          <span>Ausstehend: <strong className={dbCounts.pending === 0 ? 'text-amber-600' : 'text-gray-800'}>{dbCounts.pending}</strong></span>
          <span>Übernommen: <strong className="text-green-700">{dbCounts.approved}</strong></span>
          <span>Abgelehnt: <strong className="text-gray-600">{dbCounts.rejected}</strong></span>
          <span>Gesamt: <strong>{dbCounts.total}</strong></span>
        </div>
      )}

      {/* Filter + bulk approve */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
          {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={cn(
                'px-3 py-1.5 transition-colors',
                filterStatus === s ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
              )}
            >
              {s === 'pending' ? 'Ausstehend' : s === 'approved' ? 'Übernommen' : s === 'rejected' ? 'Abgelehnt' : 'Alle'}
            </button>
          ))}
        </div>

        {filterStatus === 'pending' && candidates.length > 0 && (
          <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
            <span className="text-xs text-gray-500">Min. Score:</span>
            <select
              value={minScore}
              onChange={(e) => setMinScore(parseFloat(e.target.value))}
              className="text-xs border border-gray-200 rounded px-2 py-1"
            >
              <option value={0.5}>50%</option>
              <option value={0.6}>60%</option>
              <option value={0.7}>70%</option>
              <option value={0.8}>80%</option>
            </select>
            <Button
              variant="secondary" size="xs"
              onClick={() => approveAllMutation.mutate()}
              disabled={approveAllMutation.isPending}
              leadingIcon={approveAllMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <CheckCheck className="w-3.5 h-3.5" />}
            >
              {approveAllMutation.isPending ? 'Übernehme…' : `Alle ≥ ${Math.round(minScore * 100)}% übernehmen`}
            </Button>
            {approveAllMutation.isSuccess && (
              <span className="text-xs text-green-600 font-medium">{approveAllMutation.data?.approved} übernommen</span>
            )}
            {approveAllMutation.isSuccess && (approveAllMutation.data?.errors?.length ?? 0) > 0 && (
              <details className="text-red-600 text-xs">
                <summary className="cursor-pointer font-medium">
                  {approveAllMutation.data!.errors.length} Fehler beim Übernehmen
                </summary>
                <ul className="mt-1 space-y-0.5 max-h-24 overflow-y-auto list-disc list-inside">
                  {approveAllMutation.data!.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
            {approveAllMutation.isError && (
              <span className="text-xs text-red-600">{approveAllMutation.error?.message}</span>
            )}
          </div>
        )}
      </div>

      {/* Individual approve error */}
      {approveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Fehler beim Übernehmen:</strong> {approveError}
        </div>
      )}

      {/* Candidate list */}
      {isLoading ? (
        <LoadingState label="Kandidaten laden…" />
      ) : error ? (
        <ErrorState title="Fehler beim Laden" description={(error as Error).message} />
      ) : candidates.length === 0 ? (
        <EmptyState
          icon={<Database className="w-8 h-8 text-gray-300" />}
          title={filterStatus === 'pending' ? 'Keine ausstehenden Kandidaten' : 'Keine Einträge'}
          description={
            filterStatus === 'pending'
              ? 'Starte einen Scan, um Angebote auf gute RAG-Kandidaten zu prüfen.'
              : 'Noch keine Einträge in dieser Kategorie.'
          }
        />
      ) : (
        <div className="space-y-2">
          {filterStatus === 'pending' && (
            <p className="text-xs text-gray-500">{candidates.length} Vorschlag{candidates.length !== 1 ? 'schläge' : ''} warten auf Freigabe</p>
          )}
          {candidates.map((c) => (
            <CandidateRow
              key={c.id}
              candidate={c}
              onApprove={handleApprove}
              onReject={handleReject}
              approving={actioningIds.has(c.id) && approveMutation.isPending}
              rejecting={actioningIds.has(c.id) && rejectMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  )
}
